import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container, Typography, Box, List, ListItem, ListItemButton,
  ListItemText, ListItemAvatar, Avatar, Badge, CircularProgress,
  Alert, TextField, Button, Paper, IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MessageIcon from "@mui/icons-material/Message";
import { inquiries as inquiriesApi } from "../api/endpoints";
import type { Inquiry, InquiryMessage, User } from "../types";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import PageHeader from "../components/PageHeader";
import { NAVY, PAGE_BG_GRADIENT, CARD_TINT_GRADIENT } from "../theme/theme";
import { alpha } from "@mui/material/styles";

// Deterministic accent per sender — same person, same color, everywhere in
// the list — so conversations read as distinct at a glance instead of one
// flat navy wall.
const SENDER_ACCENTS: { solid: string; gradient: string }[] = [
  { solid: "#0F2156", gradient: "linear-gradient(135deg, #1a3a7a 0%, #0F2156 100%)" },
  { solid: "#B91C2C", gradient: "linear-gradient(135deg, #DC2626 0%, #B91C2C 100%)" },
  { solid: "#B9781A", gradient: "linear-gradient(135deg, #E8A020 0%, #B9781A 100%)" },
  { solid: "#15803D", gradient: "linear-gradient(135deg, #22C55E 0%, #15803D 100%)" },
  { solid: "#6D28D9", gradient: "linear-gradient(135deg, #A78BFA 0%, #6D28D9 100%)" },
  { solid: "#0E7490", gradient: "linear-gradient(135deg, #22D3EE 0%, #0E7490 100%)" },
];
function senderAccent(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return SENDER_ACCENTS[hash % SENDER_ACCENTS.length];
}

export default function Messages() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Route to the post/company a thread originated from.
  const sourcePath = (inq: Inquiry) =>
    inq.entity_type === "company" ? `/app/company/${inq.entity_id}` : `/app/post/${inq.entity_id}`;
  const [list, setList] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Inquiry | null>(null);
  const [messages, setMessages] = useState<InquiryMessage[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // A masked (anonymous-post) counterparty comes back from the API with id 0.
  const isMasked = (u?: User | null) => !!u && u.id === 0;
  const nameOf = (u?: User | null) =>
    !u ? t("messages.unknown") : isMasked(u) ? t("common.anonymous") : `${u.first_name} ${u.last_name}`;
  const letterOf = (u?: User | null) => (!u ? "?" : isMasked(u) ? "A" : u.first_name.charAt(0));

  useEffect(() => {
    inquiriesApi.list(1, 50)
      .then((res) => setList(res.data ?? []))
      .catch((err) => setError(err instanceof Error ? err.message : t("common.noResults")))
      .finally(() => setLoading(false));
  }, []);

  const openThread = async (inq: Inquiry) => {
    setSelected(inq);
    setMsgLoading(true);
    try {
      const msgs = await inquiriesApi.listMessages(inq.id);
      setMessages(msgs);
      await inquiriesApi.markRead(inq.id);
      // Clear unread count locally
      setList((prev) => prev.map((i) => i.id === inq.id ? { ...i, unread_count: 0 } : i));
    } catch { /* ignore */ }
    finally { setMsgLoading(false); }
  };

  const handleSend = async () => {
    if (!selected || !text.trim()) return;
    setSending(true);
    const optimistic: InquiryMessage = {
      id: Date.now(),
      inquiry_id: selected.id,
      sender_id: user!.id,
      receiver_id: selected.sender_id === user!.id ? selected.receiver_id : selected.sender_id,
      message: text.trim(),
      is_read: false,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setText("");
    try {
      const sent = await inquiriesApi.sendMessage(selected.id, optimistic.message);
      if (sent) {
        setMessages((prev) => prev.map((m) => m.id === optimistic.id ? sent : m));
      }
    } catch { /* ignore */ }
    finally {
      setSending(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  };

  useEffect(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [messages]);

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Container maxWidth="sm" sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  }

  // Thread view
  if (selected) {
    const otherUser = selected.sender_id === user?.id ? selected.receiver : selected.sender;
    return (
      <Container maxWidth={false} disableGutters sx={{ height: "calc(100vh - 120px)" }}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Thread header */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            p: 1.5,
            overflow: "hidden",
            color: "white",
            background: "linear-gradient(135deg, #0F2156 0%, #1a3a7a 55%, rgba(185,28,44,0.92) 100%)",
            "&::before": {
              content: '""',
              position: "absolute",
              top: -40, right: -20,
              width: 120, height: 120,
              borderRadius: "50%",
              background: alpha("#E8A020", 0.18),
              filter: "blur(30px)",
              pointerEvents: "none",
            },
          }}
        >
          <IconButton onClick={() => setSelected(null)} size="small" sx={{ color: "white", position: "relative", zIndex: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ width: 32, height: 32, ml: 1, mr: 1, bgcolor: alpha("#fff", 0.2), color: "white", fontSize: "0.85rem", position: "relative", zIndex: 1 }}>
            {letterOf(otherUser)}
          </Avatar>
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {nameOf(otherUser)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            startIcon={<OpenInNewIcon sx={{ fontSize: "0.9rem !important" }} />}
            onClick={() => navigate(sourcePath(selected))}
            sx={{ fontSize: "0.68rem", textTransform: "none", color: alpha("#fff", 0.85), flexShrink: 0, position: "relative", zIndex: 1, "&:hover": { color: "white", bgcolor: alpha("#fff", 0.1) } }}
          >
            {selected.entity_type === "company" ? t("messages.viewCompany") : t("messages.viewPost")}
          </Button>
        </Box>

        {/* Messages */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2, backgroundImage: PAGE_BG_GRADIENT }}>
          {msgLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} /></Box>
          ) : (
            messages.map((msg) => {
              const isMine = msg.sender_id === user?.id;
              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: "flex",
                    justifyContent: isMine ? "flex-end" : "flex-start",
                    mb: 1,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1,
                      maxWidth: { xs: "75%", sm: "60%", md: 480 },
                      background: isMine
                        ? "linear-gradient(135deg, #DC2626 0%, #B91C2C 100%)"
                        : "linear-gradient(135deg, #1a3a7a 0%, #0F2156 100%)",
                      color: "white",
                      borderRadius: isMine ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                      border: "none",
                    }}
                  >
                    <Typography variant="body2">{msg.message}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mt: 0.3, fontSize: "0.65rem" }}>
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </Typography>
                  </Paper>
                </Box>
              );
            })
          )}
          <div ref={bottomRef} />
        </Box>

        {/* Input */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            p: 1.5,
            borderTop: `1px solid ${alpha(NAVY, 0.12)}`,
            bgcolor: "white",
          }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder={t("messages.writeMessage")}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 20,
                bgcolor: alpha(NAVY, 0.04),
                fontWeight: 600,
                "& fieldset": { border: `2px solid ${alpha(NAVY, 0.45)}` },
                "&:hover fieldset": { border: `2px solid ${alpha(NAVY, 0.6)}` },
                "&.Mui-focused fieldset": { border: `2px solid ${NAVY}` },
              },
              "& .MuiOutlinedInput-input": {
                fontWeight: 600,
                color: NAVY,
                "&::placeholder": { color: alpha(NAVY, 0.6), opacity: 1, fontWeight: 600 },
              },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={sending || !text.trim()}
            sx={{
              bgcolor: text.trim() ? "#B91C2C" : NAVY,
              color: "white",
              boxShadow: text.trim() ? `0 3px 10px ${alpha("#B91C2C", 0.4)}` : `0 3px 10px ${alpha(NAVY, 0.3)}`,
              "&:hover": { bgcolor: text.trim() ? "#8B0F1B" : "#1a3a7a" },
              "&.Mui-disabled": { bgcolor: alpha(NAVY, 0.3), color: "white" },
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      </Container>
    );
  }

  // List view
  return (
    <>
    <PageHeader title={t("messages.title")} icon={<MessageIcon sx={{ color: "white", fontSize: 22 }} />} />
    <Container maxWidth="md" sx={{ py: 2 }}>
      {list.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" py={6}>
          {t("messages.noMessages")}
        </Typography>
      ) : (
        <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {list.map((inq) => {
              const other = inq.sender_id === user?.id ? inq.receiver : inq.sender;
              const hasUnread = (inq.unread_count ?? 0) > 0;
              const accent = senderAccent(nameOf(other));
              return (
                <ListItem key={inq.id} disablePadding
                  sx={{
                    border: `2px solid ${hasUnread ? "#B91C2C" : alpha(accent.solid, 0.45)}`,
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                    <ListItemButton
                      onClick={() => openThread(inq)}
                      sx={{
                        borderLeft: `5px solid ${hasUnread ? "#B91C2C" : accent.solid}`,
                        pl: 1.75,
                        backgroundImage: CARD_TINT_GRADIENT,
                        backgroundColor: "#FFFFFF",
                        "&:hover": {
                          backgroundColor: alpha(accent.solid, 0.06),
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Badge badgeContent={inq.unread_count} color="primary" invisible={!hasUnread}>
                          <Avatar sx={{ background: accent.gradient, width: 40, height: 40 }}>
                            {letterOf(other)}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        secondaryTypographyProps={{ component: "div" }}
                        primary={
                          <Typography variant="subtitle2" fontWeight={hasUnread ? 700 : 400}>
                            {nameOf(other)}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 0.3 }}>
                            <Box
                              component="span"
                              sx={{
                                fontSize: "0.62rem",
                                fontWeight: 700,
                                color: "white",
                                px: 0.9,
                                py: 0.15,
                                borderRadius: 10,
                                backgroundImage: inq.entity_type === "company"
                                  ? "linear-gradient(135deg, #1a3a7a 0%, #0F2156 100%)"
                                  : "linear-gradient(135deg, #E8A020 0%, #B9781A 100%)",
                              }}
                            >
                              {inq.entity_type === "company" ? t("messages.businessInquiry") : t("messages.postInquiry")}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {inq.last_message_at
                                ? new Date(inq.last_message_at).toLocaleDateString()
                                : new Date(inq.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                </ListItem>
              );
            })}
        </List>
      )}
    </Container>
    </>
  );
}
